import React, { useState, useEffect, useCallback } from 'react';
import { useBrand } from '../../context/BrandContext';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../../services/categoryService';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubIcon,
} from '@mui/icons-material';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * Category Management (SP-1b) — per-brand two-level taxonomy editor.
 * Add / rename (cascades to assigned products, confirm-gated) / reorder /
 * delete (blocked when products are still assigned) categories & sub-categories.
 * Brand comes from the global BrandContext (same selector the product pages use).
 */
export default function CategoryManagement() {
  const { selectedBrand } = useBrand();
  const { confirm, confirmDialogProps } = useConfirmDialog();
  const brandId = selectedBrand?.id;

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [addSubFor, setAddSubFor] = useState(null); // category name currently adding a sub to
  const [newSub, setNewSub] = useState('');
  const [editing, setEditing] = useState(null); // { id, value }

  const load = useCallback(async () => {
    if (!brandId) { setTree([]); return; }
    setLoading(true); setError('');
    try {
      const res = await getCategories(brandId);
      setTree(res.categories || []);
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const errFrom = (e, fallback) => {
    const d = e.response?.data;
    if (d?.error) {
      let msg = d.error;
      if (d.products !== undefined) msg += ` — ${d.products} product${d.products === 1 ? '' : 's'} still assigned`;
      else if (d.sub_categories !== undefined) msg += ` — ${d.sub_categories} active sub-categor${d.sub_categories === 1 ? 'y' : 'ies'}`;
      return msg;
    }
    return e.message || fallback;
  };

  // Run a mutation, then reload the tree. Surfaces 409/400 messages (incl. counts).
  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); await load(); }
    catch (e) { setError(errFrom(e, 'Action failed')); }
    finally { setBusy(false); }
  };

  const handleAddCategory = () => {
    const name = newCat.trim();
    if (!name) return;
    run(async () => { await createCategory(brandId, { category: name }); setNewCat(''); });
  };

  const handleAddSub = (category) => {
    const name = newSub.trim();
    if (!name) return;
    run(async () => {
      await createCategory(brandId, { category, sub_category: name });
      setNewSub(''); setAddSubFor(null);
    });
  };

  // Rename via inline edit. Confirm-gated (Decision A) when products would move.
  const saveEdit = async (node, isSub) => {
    const name = (editing?.value || '').trim();
    const current = isSub ? node.sub_category : node.category;
    if (!editing || !name || name === current) { setEditing(null); return; }
    const affected = node.product_count || 0;
    if (affected > 0) {
      const proceed = await confirm({
        title: `Rename ${isSub ? 'sub-category' : 'category'}`,
        message: `This will rename ${affected} assigned product${affected === 1 ? '' : 's'} from "${current}" to "${name}". Continue?`,
        confirmText: 'Rename',
        severity: 'warning',
      });
      if (!proceed) { setEditing(null); return; }
    }
    setEditing(null);
    run(async () => {
      await updateCategory(brandId, node.id, isSub ? { sub_category: name } : { category: name });
    });
  };

  // Delete = soft-delete; server returns 409 (with count) if still in use (Decision B).
  const handleDelete = async (node, isSub) => {
    const label = isSub ? node.sub_category : node.category;
    const proceed = await confirm({
      title: `Delete ${isSub ? 'sub-category' : 'category'}`,
      message: `Delete "${label}"? This is blocked if any products are still assigned to it.`,
      confirmText: 'Delete',
      severity: 'danger',
    });
    if (!proceed) return;
    run(async () => { await deleteCategory(brandId, node.id); });
  };

  // Up/down reorder: reposition in the sibling list, then persist sort_order = new index.
  const reorder = (siblings, index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= siblings.length) return;
    const arr = siblings.slice();
    const [moved] = arr.splice(index, 1);
    arr.splice(j, 0, moved);
    run(async () => {
      for (let i = 0; i < arr.length; i++) {
        if ((arr[i].sort_order || 0) !== i) {
          await updateCategory(brandId, arr[i].id, { sort_order: i });
        }
      }
    });
  };

  const editField = (node, isSub) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField
        size="small"
        autoFocus
        value={editing.value}
        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(node, isSub); if (e.key === 'Escape') setEditing(null); }}
        sx={{ minWidth: 200 }}
      />
      <IconButton size="small" color="primary" onClick={() => saveEdit(node, isSub)}><CheckIcon fontSize="small" /></IconButton>
      <IconButton size="small" onClick={() => setEditing(null)}><CloseIcon fontSize="small" /></IconButton>
    </Stack>
  );

  const rowActions = (node, isSub, siblings, index) => (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Tooltip title="Move up"><span><IconButton size="small" disabled={busy || index === 0} onClick={() => reorder(siblings, index, -1)}><UpIcon fontSize="small" /></IconButton></span></Tooltip>
      <Tooltip title="Move down"><span><IconButton size="small" disabled={busy || index === siblings.length - 1} onClick={() => reorder(siblings, index, 1)}><DownIcon fontSize="small" /></IconButton></span></Tooltip>
      <Tooltip title="Rename"><span><IconButton size="small" disabled={busy || !node.id} onClick={() => setEditing({ id: node.id, value: isSub ? node.sub_category : node.category })}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
      <Tooltip title="Delete"><span><IconButton size="small" color="error" disabled={busy || !node.id} onClick={() => handleDelete(node, isSub)}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Category Management</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Two-level Category → Sub-category taxonomy{selectedBrand ? ` for ${selectedBrand.name || selectedBrand.brand_name || `brand ${brandId}`}` : ''}.
        Renaming a category updates every assigned product; deleting is blocked while products are still assigned.
      </Typography>

      {!brandId && <Alert severity="info" sx={{ mt: 2 }}>Select a brand to manage its categories.</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {brandId && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="New category"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }}
              sx={{ flex: 1 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} disabled={busy || !newCat.trim()} onClick={handleAddCategory}>
              Add category
            </Button>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
          ) : tree.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>No categories yet for this brand. Add one above.</Alert>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {tree.map((cat, ci) => (
                <Paper key={cat.id || cat.category} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CategoryIcon color="action" fontSize="small" />
                    {editing && editing.id === cat.id ? editField(cat, false) : (
                      <Typography sx={{ fontWeight: 600 }}>{cat.category}</Typography>
                    )}
                    <Chip size="small" label={`${cat.product_count} product${cat.product_count === 1 ? '' : 's'}`} sx={{ ml: 0.5 }} />
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => { setAddSubFor(addSubFor === cat.category ? null : cat.category); setNewSub(''); }}>
                      Sub-category
                    </Button>
                    {rowActions(cat, false, tree, ci)}
                  </Box>

                  {addSubFor === cat.category && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, ml: 4 }}>
                      <TextField
                        size="small"
                        autoFocus
                        label={`New sub-category under "${cat.category}"`}
                        value={newSub}
                        onChange={(e) => setNewSub(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddSub(cat.category); if (e.key === 'Escape') setAddSubFor(null); }}
                        sx={{ flex: 1 }}
                      />
                      <Button size="small" variant="contained" disabled={busy || !newSub.trim()} onClick={() => handleAddSub(cat.category)}>Add</Button>
                    </Box>
                  )}

                  {cat.sub_categories.map((sub, si) => (
                    <Box key={sub.id || sub.sub_category} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, ml: 4 }}>
                      <SubIcon color="disabled" fontSize="small" />
                      {editing && editing.id === sub.id ? editField(sub, true) : (
                        <Typography variant="body2">{sub.sub_category}</Typography>
                      )}
                      <Chip size="small" variant="outlined" label={`${sub.product_count}`} />
                      <Box sx={{ flex: 1 }} />
                      {rowActions(sub, true, cat.sub_categories, si)}
                    </Box>
                  ))}
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
